<script setup lang="ts">
/**
 * PendingApprovalBanner — shown on the resident dashboard when isPending === true.
 * Amber tone (not teal — amber = pending/warning, per CLAUDE.md severity color map).
 * Mobile-first: stacks at 390px.
 */
defineProps<{
  userName?: string;
}>();
</script>

<template>
  <div
    class="pending-approval-banner"
    role="status"
    aria-live="polite"
    data-testid="pending-approval-banner"
  >
    <div class="pab-icon">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    </div>
    <div class="pab-body">
      <div class="pab-title">
        Account pending board approval
      </div>
      <div class="pab-sub">
        <template v-if="userName">Hi {{ userName }}!</template>
        Your account is created — the board is reviewing your application.
        You'll get an email the moment you're approved.
        Typically <strong>1–2 business days</strong>.
      </div>
    </div>
    <a href="/portal" class="pab-action">Report issue →</a>
  </div>
</template>

<style scoped>
.pending-approval-banner {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 20px 24px;
  background: var(--amber-100);
  border: 1px solid var(--amber-200, var(--amber-100));
  border-radius: var(--r-xl, 16px);
  margin-bottom: 24px;
}
.pab-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: white;
  color: var(--amber-700);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.pab-icon svg { width: 22px; height: 22px; }
.pab-body { flex: 1; }
.pab-title { font-size: 14.5px; font-weight: 700; color: var(--amber-800, #92400e); margin-bottom: 4px; }
.pab-sub { font-size: 13px; color: var(--amber-700); line-height: 1.55; }
.pab-action {
  font-size: 13px;
  font-weight: 600;
  color: var(--navy-700);
  white-space: nowrap;
  text-decoration: none;
  margin-top: 2px;
}
.pab-action:hover { text-decoration: underline; }
@media (max-width: 600px) {
  .pending-approval-banner { flex-direction: column; gap: 12px; padding: 16px; }
  .pab-action { align-self: flex-start; }
}
</style>
